import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Clock, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../contexts/CartContext';
import { API, gbp } from '../lib/shop';

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const { clearCart } = useCart();
  const [order, setOrder] = useState(null);
  const [state, setState] = useState(sessionId ? 'checking' : 'error');
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) return;
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/shop/orders/status/${sessionId}`);
        setOrder(data);
        if (data.payment_status === 'paid') { setState('paid'); clearCart(); return; }
        if (['expired', 'failed'].includes(data.payment_status)) { setState('failed'); return; }
      } catch { /* keep polling */ }
      attempts.current += 1;
      if (attempts.current >= 10) { setState('timeout'); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen pt-32 pb-20 px-4 bg-gray-50" data-testid="payment-success-page">
      <div className="max-w-2xl mx-auto bg-white rounded-3xl p-8 sm:p-12 border border-gray-100 text-center">
        {state === 'checking' && (<>
          <Clock className="w-16 h-16 text-pink-400 mx-auto mb-6 animate-pulse" />
          <h1 className="text-3xl font-bold text-gray-900 mb-3" data-testid="payment-checking">Confirming your payment...</h1>
          <p className="text-gray-600">Please wait a moment while we confirm your payment with Stripe.</p>
        </>)}
        {state === 'paid' && order && (<>
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-6" />
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3" data-testid="payment-paid">Thank you, {order.customer.name.split(' ')[0]}!</h1>
          <p className="text-gray-600 mb-6">Your order is confirmed. A confirmation email is on its way to <strong>{order.customer.email}</strong>.</p>
          <div className="bg-pink-50 rounded-2xl p-5 text-left mb-8">
            <p className="text-sm text-gray-600 mb-3">Order number <span className="font-bold text-gray-900 text-base" data-testid="order-number">{order.order_number}</span></p>
            <ul className="divide-y divide-pink-100">
              {order.items.map((i, idx) => (
                <li key={idx} className="flex justify-between py-2 text-sm text-gray-800"><span>{i.name} × {i.quantity}</span><span className="font-semibold">{gbp(i.price * i.quantity)}</span></li>
              ))}
            </ul>
            <div className="flex justify-between font-bold text-gray-900 pt-3 border-t border-pink-200 mt-2"><span>Total paid</span><span data-testid="order-total">{gbp(order.total)}</span></div>
          </div>
        </>)}
        {(state === 'failed' || state === 'error') && (<>
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-gray-900 mb-3" data-testid="payment-failed">Payment not completed</h1>
          <p className="text-gray-600 mb-8">Your payment was not completed and you have not been charged. Your cart has been kept so you can try again.</p>
        </>)}
        {state === 'timeout' && (<>
          <Clock className="w-16 h-16 text-amber-500 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-gray-900 mb-3" data-testid="payment-timeout">Still confirming</h1>
          <p className="text-gray-600 mb-8">Your payment is taking longer than usual to confirm. If you were charged, you will receive a confirmation email shortly. Order reference: {order?.order_number}</p>
        </>)}
        <div className="flex flex-wrap gap-3 justify-center">
          <Button asChild className="bg-pink-500 hover:bg-pink-600 text-white rounded-full"><Link to="/shop" data-testid="success-continue-shopping">Continue shopping</Link></Button>
          <Button asChild variant="outline" className="rounded-full"><Link to="/" data-testid="success-home">Back to home</Link></Button>
        </div>
      </div>
    </div>
  );
}
